import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { LoaderController } from '@adornis/chemistry/controllers/loader-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import '@adornis/writer/x-writer-toolbar.js';
import { hasGlobalRole } from '@ils/permissions/hasRole.js';
import { ExamType, UserRole } from '@ils/static/enums.js';
import { watch } from '@lit-labs/preact-signals';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { filter, map, take } from 'rxjs';
import type { ILSUser } from '../db/ils-user.js';
import './components/ils-loader.js';
import './components/ils-login.js';
import { isOnline } from './global-state.js';
import { embedRouter } from './pages/ils-embed.js';
import { startRouter } from './pages/ils-start.js';
import './pages/index.js';

// want to use this but currentUser not working properly atm for isReady
// LoaderController.waitFor(currentUser<ILSUser>().isReady.pipe(filter(Boolean), take(1)));
void LoaderController.waitFor(
  currentUser().pipe(
    map(user => user !== undefined),
    filter(Boolean),
    take(1),
  ),
);

const authGuard = (render: () => any) =>
  authenticationGuard(
    render,
    () => html`
      <ils-login
        ${css({ width: '100%' })}
        @login=${(e: CustomEvent<ILSUser>) => {
          setTimeout(() => goTo('/start'), 0);
          return XSnackbar.show('Willkommen zurück!');
        }}
        @error=${() => XSnackbar.show('Login fehlgeschlagen.')}
      ></ils-login>
    `,
  );

const mainRouter = makeRouter({
  root: {
    path: '/',
    render: () =>
      authGuard(() => {
        goTo('/start');
        return html` <x-text> root </x-text> `;
      }),
  },
  start: {
    path: '/start*',
    render: () => authGuard(() => html` <ils-start></ils-start> `),
    subRouter: startRouter,
  },
  token: {
    path: '/token',
    render: () => html` <ils-token></ils-token> `,
  },
  resetPassword: {
    path: '/reset-password',
    render: () => html` <ils-reset-password></ils-reset-password> `,
  },
  embed: {
    path: '/embed*',
    render: () => html` <ils-embed></ils-embed> `,
    subRouter: embedRouter,
  },
  paymentProcessing: {
    path: '/payment-processing/:examType?',
    render: params =>
      html` <ils-payment-processing exam-type=${params.examType ?? ExamType.FAMED}></ils-payment-processing> `,
  },
  exam: {
    path: '/exam',
    render: () => html` <ils-exam></ils-exam> `,
  },
}).init();

@customElement('main-element')
export class MainElement extends ChemistryLitElement {
  override styles() {
    return [
      {
        ':host': { background: window.location.href.includes('/embed') ? 'transparent' : this.colors.light },
      },
    ];
  }

  @state() user = new RXController(this, currentUser<ILSUser>());
  @state() userLoading = new RXController(this, currentUser<ILSUser>().isLoading);

  @state() private readonly _isOnline = new RXController(this, isOnline);

  override render() {
    return html`
      <x-flex horizontal ${css({ width: '100%', height: '100%', overflow: 'auto' })}>
        ${watch(mainRouter.outlet)}
      </x-flex>
      ${
        // TODO this has to stay until we migrate away from the ancient x-writer. This is still used in the osce editor and maybe more places
        hasGlobalRole(this.user.value, UserRole.ADMIN) ? html` <x-writer-toolbar></x-writer-toolbar> ` : nothing
      }
      ${!this._isOnline.value ? this.offlineOverlay() : nothing}
    `;
  }

  private offlineOverlay() {
    return html`
      <x-flex
        center
        crossaxis-center
        ${css({
          position: 'absolute',
          top: '0',
          right: '0',
          zIndex: 1000,
          height: '100%',
          width: '100%',
          background: 'rgba(255,255,255,0.75)',
        })}
      >
        <x-empty-placeholder .text=${'Bitte überprüfen Sie Ihre Verbindung'} .img=${'wifi_off'}></x-empty-placeholder>
      </x-flex>
    `;
  }
}
